import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { recommendationsJsonToHtml } from './recommendationsHtml';

import type { AiResultNotification } from '@/api/ai-gateway/aiHub';

import { managerAssessmentService } from '@/api/cycle-evaluation-services/manager-assessment';
import { useAiResultContext } from '@/hooks/ai/AiResultProvider';

// Give up waiting for the SignalR result after this long so the modal never hangs on a lost/failed job.
const RESULT_TIMEOUT_MS = 90_000;

/**
 * Generates the "Recommendations for your development" text: posts the manager's two answers, waits for the
 * AI result over SignalR (matched by correlationId), and returns it as ready-to-store HTML. Returns null on
 * failure/timeout so the modal can surface an error instead of hanging.
 */
export function useRecommendationsGeneration(cycleInstanceId: string | undefined) {
    const { subscribe } = useAiResultContext();
    const { i18n } = useTranslation();

    const generate = useCallback(
        async (feeling: string, priority: string): Promise<string | null> => {
            if (!cycleInstanceId) return null;

            const correlationId = crypto.randomUUID();

            // Arm the subscription before posting, so a fast result can't slip through before we listen.
            const resultPromise = new Promise<AiResultNotification | null>((resolve) => {
                let unsubscribe = () => {};
                const timeout = window.setTimeout(() => {
                    unsubscribe();
                    resolve(null);
                }, RESULT_TIMEOUT_MS);
                unsubscribe = subscribe((notification) => {
                    if (notification.correlationId !== correlationId) return;
                    window.clearTimeout(timeout);
                    unsubscribe();
                    resolve(notification);
                });
            });

            try {
                await managerAssessmentService.generateRecommendations({
                    cycleInstanceId,
                    correlationId,
                    overallFeeling: feeling,
                    developmentPriority: priority,
                    language: i18n.language,
                });
            } catch {
                return null;
            }

            const notification = await resultPromise;
            if (!notification?.success || !notification.content) return null;

            return recommendationsJsonToHtml(notification.content);
        },
        [cycleInstanceId, subscribe, i18n.language],
    );

    return { generate };
}
